import React, { useEffect, useRef, useState, useContext } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import { getIsIntegrated, getStatus, acceptOrder, declineOrder } from "requests/appraisalPort";
import { CASE_HOLD_UPDATE, APPRAISAL_PORT_MESSAGE_ADD, APPRAISAL_PORT_ACCEPT_DECLINE_ORDER } from "typeConstants/rights";
import OnHold from "components/Homeowner/CaseForms/Actions/OnHold/AppraisalPort/OnHold";
import Message from "components/Homeowner/CaseForms/Actions/OnHold/AppraisalPort/Message";
import { MSpinner, MCollapsibleCard } from "components/Shared";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import WebConfig from 'middlewares/WebConfig';

export const APPRAISAL_PORT_HOLD_TAB_ID = "APPRAISAL_PORT_HOLD_TAB_ID";
export const APPRAISAL_PORT_MESSAGE_TAB_ID = "APPRAISAL_PORT_MESSAGE_TAB_ID";
const appraisalPortOnHoldCodes = ["H", "OH", "HI"];
// Old Appraisal Port API doesn't support on/off hold calls
const oldApiCustomerNumbers = [ "1990" ];

const AppraisalPort = ({
  controlNumber,
  caseHeader,
  canUpdateOnHold,
  canSendMessage,
  openCaseActionTab,
  closeTab,
  canAcceptDeclineOrders,
}) => {
  const [isIntegrated, setIsIntegrated] = useState(false);
  const [status, setStatus] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showStatusNotice, setShowStatusNotice] = useState(false);
  const mounted = useRef(true);
  const isOnHold = appraisalPortOnHoldCodes.includes(status.code);
  const disableOnOffHold = !canUpdateOnHold || oldApiCustomerNumbers.includes(caseHeader.policyInfo.customerInfo.customerNumber);
  const dialog = useContext(DialogContext);

  useEffect(() => {
    getIsIntegrated(caseHeader.policyInfo.customerInfo.customerNumber, getAppraisalPortDetails, mounted);
    return () => {
      mounted.current = false;
    };
  }, []);

  const updateStatus = () => {
    setUpdatingStatus(true);
    getStatus(controlNumber, handleStatus, handleStatusError, mounted);
  };

  const handleStatus = (response) => {
    setStatus(response);
    setUpdatingStatus(false);
    setShowStatusNotice(false);
  };

  const handleStatusError = () => setUpdatingStatus(false);

  const getAppraisalPortDetails = (response) => {
    if (response) {
      setIsIntegrated(response);
      updateStatus();
      setExpanded(true);
    }
  };

  const handleOnHoldClick = () => {
    openCaseActionTab(
      <OnHold onSuccessCallback={onHoldChangedCallback} isOnHold={isOnHold} />,
      isOnHold ? "Off Hold Request" : "On Hold Request",
      APPRAISAL_PORT_HOLD_TAB_ID
    );
  };

  const handleMessageClick = () => {
    openCaseActionTab(
      <Message onSuccessCallback={onMessageCallback} />,
      "Message Request",
      APPRAISAL_PORT_MESSAGE_TAB_ID
    );
  };

  const onHoldChangedCallback = () => {
    closeTab(APPRAISAL_PORT_HOLD_TAB_ID);
    setShowStatusNotice(true);
    // Using setTimeout here as we have to wait for the Appraisal Port system to update
    setTimeout(updateStatus, 5000);
  };

  const onMessageCallback = () => {
    closeTab(APPRAISAL_PORT_MESSAGE_TAB_ID);
  };

  const handleAcceptClick = () => {
    dialog.showYesNo(
      "Accept Appraisal Port Order",
      "Are you sure you want to Accept this order?",
      onAccept,
      null,
      "Yes, Accept",
      "No, Cancel"
    );
  };

  const onAccept = () => {
    setShowStatusNotice(true);
    acceptOrder(controlNumber);
    setTimeout(updateStatus, 5000); // Wait Appraisal Port system to update
  };

  const handleDeclineClick = () => {
    dialog.showYesNo(
      "Decline Appraisal Port Order",
      "Are you sure you want to Decline this order?",
      onDecline,
      null,
      "Yes, Decline",
      "No, Cancel"
    );
  };

  const onDecline = () => {
    setShowStatusNotice(true);
    declineOrder(controlNumber);
    setTimeout(updateStatus, 5000); // Wait Appraisal Port system to update
  };

  const handleAppraisalPortClick = () => {
    let appraisalPortUrl = "https://www.appraisalport.com/";
    if (WebConfig.environment !== 'production') {
      appraisalPortUrl =  "https://uat.appraisalport.com/";
    }
    let orderId = caseHeader.policyInfo.caseAttributes?.appraisalPort_OrderId;
    if (orderId) {
      appraisalPortUrl += `Dashboard/#${orderId}`;
    }
    window.open(appraisalPortUrl, "_blank");
  };

  if (!isIntegrated) {
    return null;
  }

  if (updatingStatus) {
    return <MSpinner />;
  }

  const isChaseServicingAccount = caseHeader.policyInfo.customerInfo.customerNumber === "1990";
  // Chase Servicing account 1990 is currently the only account where we want to manually accept or decline
  const showAcceptDeclineButtons = isChaseServicingAccount;
  const acceptDeclineEnabled = canAcceptDeclineOrders && status?.code === "A"; // ASSIGNED_TO_PROVIDER
  // Chase Servicing is on an old version of AppraisalPort and placing orders on hold must be done there
  const showAppraisalPortButton = isChaseServicingAccount;

  return (
    <MCollapsibleCard
      title="Appraisal Port Actions"
      expanded={expanded}
      onExpandedChange={setExpanded}
      styledHeader={false}
      wrapperClassName=""
    >
      {status &&
        <h4>
          {`Status: ${status.name} (${status.code})`}
          <i
            className="md-icon material-icons right"
            title="Refresh Status"
            onClick={updateStatus}
          >
            refresh
          </i>
        </h4>}
      {showStatusNotice &&
        <p>Status change request sent. This may take a moment to update in the Appraisal Port system. Please check back in a few moments.</p>}
      <span className={"case-action"}>
        <Button
          id="on-hold-action-button"
          onClick={handleOnHoldClick}
          disabled={disableOnOffHold || isOnHold}
        >
          On Hold
        </Button>
      </span>
      <span className={"case-action"}>
        <Button
          id="off-hold-action-button"
          onClick={handleOnHoldClick}
          disabled={disableOnOffHold || !isOnHold}
        >
          Off Hold
        </Button>
      </span>
      {showAcceptDeclineButtons && <>
        <span className={"case-action"}>
          <Button
            id="message-action-button"
            onClick={handleAcceptClick}
            disabled={!acceptDeclineEnabled}
          >
            Accept Order
          </Button>
        </span>
        <span className={"case-action"}>
          <Button
            id="message-action-button"
            onClick={handleDeclineClick}
            disabled={!acceptDeclineEnabled}
          >
            Decline Order
          </Button>
        </span>
      </>}
      <span className={"case-action"}>
        <Button
          id="message-action-button"
          onClick={handleMessageClick}
          disabled={!canSendMessage}
        >
          Send Message
        </Button>
      </span>
      {showAppraisalPortButton && <span className={"case-action"}>
        <Button
          id="appraisal-port-action-button"
          onClick={handleAppraisalPortClick}
        >
          Open in AppraisalPort
        </Button>
      </span>}
    </MCollapsibleCard>
  );
};

AppraisalPort.propTypes = {
  controlNumber: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  caseHeader: PropTypes.object,
  canUpdateOnHold: PropTypes.bool,
  canSendMessage: PropTypes.bool,
  onHoldReasons: PropTypes.array,
  isFetchingHoldReasons: PropTypes.bool,
  openCaseActionTab: PropTypes.func, // Tab control method used to open a new tab
  closeTab: PropTypes.func,  // Tab control method that is called with a tab id to close the tab
  canAcceptDeclineOrders: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    controlNumber: state.caseDetails.controlNumber,
    caseHeader: state.caseForms.caseHeader,
    canUpdateOnHold: state.user.profile.rights.includes(CASE_HOLD_UPDATE),
    canSendMessage: state.user.profile.rights.includes(APPRAISAL_PORT_MESSAGE_ADD),
    canAcceptDeclineOrders: state.user.profile.rights.includes(APPRAISAL_PORT_ACCEPT_DECLINE_ORDER),
  };
};

export default connect(mapStateToProps)(AppraisalPort);